import { useEffect, useState } from "react";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import { affGet } from "@/lib/affiliateApi";
import { C } from "@/lib/brand";

// Marketing Assets — future visual asset library (images, video,
// brand kit). Today it surfaces whatever the existing /affiliate/assets
// endpoint returns; once dedicated visual content lands the BE can
// move it under /affiliate/content/marketing without an FE redeploy.
export default function AssetsMarketing() {
  const [assets, setAssets] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Try the new namespaced path first; fall back to the legacy
        // /affiliate/assets if the new one isn't deployed yet.
        let res;
        try {
          res = await affGet("/affiliate/content/marketing", { auth: true });
        } catch {
          res = await affGet("/affiliate/assets", { auth: true });
        }
        if (!cancelled) setAssets(res?.assets || []);
      } catch {
        if (!cancelled) setAssets([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (assets === null) {
    return <div style={{ padding: 64, textAlign: "center" }}><Spinner size={28} /></div>;
  }

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 24px 80px" }}>
      <Eyebrow color={C.peachDeep}>★ MARKETING ASSETS</Eyebrow>
      <Headline before="VISUALS" italic="& downloads." size={48} className="mt-4" />
      <p style={{ marginTop: 14, fontSize: 15, color: C.warmGray, maxWidth: 640, lineHeight: 1.6 }}>
        Images, video clips, and brand assets you can download and post. New drops are added regularly.
      </p>
      {assets.length === 0 ? (
        <div data-testid="marketing-empty" style={{ marginTop: 28, padding: 28, background: C.bone, fontSize: 14, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
          We're producing the first wave of visual assets now. We'll email you when they land in your portal.
        </div>
      ) : (
        <div data-testid="marketing-grid" style={{ marginTop: 28, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
          {assets.map((a, i) => (
            <article key={a.id || i} data-testid={`asset-${a.id || i}`} style={{ background: C.pure, border: `1px solid ${C.warmGray3}`, display: "flex", flexDirection: "column" }}>
              <div style={{ aspectRatio: "1 / 1", background: C.bone, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                {a.thumbnailUrl ? (
                  <img src={a.thumbnailUrl} alt={a.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <span style={{ fontSize: 36, color: C.warmGray, opacity: 0.5 }}>★</span>
                )}
              </div>
              <div style={{ padding: 16, flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.3 }}>{a.title || "Untitled"}</div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.warmGray }}>
                  {a.format || "FILE"}{a.size ? ` · ${a.size}` : ""}
                </div>
                <div style={{ flex: 1 }} />
                <a
                  href={a.downloadUrl || "#"}
                  download
                  data-testid={`asset-download-${a.id || i}`}
                  style={{
                    marginTop: 6,
                    padding: "9px 12px",
                    background: C.ink,
                    color: C.cream,
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: "0.22em",
                    textAlign: "center",
                    textDecoration: "none",
                  }}
                >
                  DOWNLOAD →
                </a>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
